import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Ticket, Plus, RefreshCw, Copy, Zap, Search, Users } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { createClientComponentClient } from '@/integrations/supabase/client'

interface PromoRow {
  id: string
  code: string
  percent_off: number | null
  amount_off_usd: number | null
  duration: string
  duration_in_months: number | null
  max_redemptions: number | null
  times_redeemed: number
  expires_at: string | null
  notes: string | null
  is_active: boolean
  created_at: string
}

const DURATION_LABEL: Record<string, string> = {
  once: 'First invoice only',
  repeating: 'Repeating',
  forever: 'Forever',
}

export function PromoManagement() {
  const supabase = createClientComponentClient()
  const [promos, setPromos] = useState<PromoRow[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [search, setSearch] = useState('')

  const [form, setForm] = useState({
    code: '',
    discountType: 'percent' as 'percent' | 'amount',
    percent_off: 95,
    amount_off_usd: 0,
    duration: 'once' as 'once' | 'repeating' | 'forever',
    duration_in_months: 12,
    max_redemptions: 20,
    expires_at: '',
    notes: '',
  })

  const fetchPromos = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('promo-admin', { body: { action: 'list' } })
      if (error) throw new Error(error.message)
      if (data?.error) throw new Error(data.error)
      setPromos((data.promos || []) as PromoRow[])
    } catch (e: any) {
      setMessage({ kind: 'err', text: e.message || 'Failed to load promos' })
    } finally {
      setLoading(false)
    }
  }, [supabase])

  useEffect(() => { fetchPromos() }, [fetchPromos])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreating(true)
    setMessage(null)
    try {
      const payload: Record<string, unknown> = {
        action: 'create',
        code: form.code.trim(),
        duration: form.duration,
        duration_in_months: form.duration === 'repeating' ? form.duration_in_months : undefined,
        max_redemptions: form.max_redemptions || undefined,
        expires_at: form.expires_at || undefined,
        notes: form.notes.trim() || undefined,
      }
      if (form.discountType === 'percent') {
        payload.percent_off = form.percent_off
      } else {
        payload.amount_off_usd = form.amount_off_usd
      }

      const { data, error } = await supabase.functions.invoke('promo-admin', { body: payload })
      if (error) throw new Error(error.message)
      if (data?.error) throw new Error(data.error)

      setMessage({ kind: 'ok', text: `Promo code ${data.promo_code} created and live at checkout.` })
      setForm({ ...form, code: '', notes: '' })
      fetchPromos()
    } catch (e: any) {
      setMessage({ kind: 'err', text: e.message || 'Failed to create promo' })
    } finally {
      setCreating(false)
    }
  }

  const handleToggle = async (p: PromoRow) => {
    try {
      const { data, error } = await supabase.functions.invoke('promo-admin', { body: { action: 'toggle', id: p.id } })
      if (error) throw new Error(error.message)
      if (data?.error) throw new Error(data.error)
      fetchPromos()
    } catch (e: any) {
      setMessage({ kind: 'err', text: e.message || 'Toggle failed' })
    }
  }

  const copy = (code: string) => {
    navigator.clipboard?.writeText(code)
    setMessage({ kind: 'ok', text: `${code} copied` })
  }

  const filtered = promos.filter(p =>
    p.code.toLowerCase().includes(search.toLowerCase()) ||
    (p.notes || '').toLowerCase().includes(search.toLowerCase())
  )

  const inputCls = 'bg-slate-900/60 border-slate-700 text-foreground rounded-xl'

  return (
    <div className="p-6 sm:p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
          <Ticket className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-xl font-black text-foreground tracking-tight">Promotion Codes</h2>
          <p className="text-xs text-muted-foreground font-medium">
            Create Stripe-backed codes — quantity-limited, %- or $-off, with duration. Works at every checkout.
          </p>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={handleCreate} className="bg-card/70 border border-border rounded-3xl p-6 space-y-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground block mb-1.5">Code (blank = auto)</label>
            <Input placeholder="SAGEPILOT95" value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} />
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground block mb-1.5">Discount</label>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setForm({ ...form, discountType: 'percent' })}
                className={`px-3 py-2 rounded-xl text-xs font-bold border ${form.discountType === 'percent' ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-muted-foreground border-border'}`}>%</button>
              <button type="button" onClick={() => setForm({ ...form, discountType: 'amount' })}
                className={`px-3 py-2 rounded-xl text-xs font-bold border ${form.discountType === 'amount' ? 'bg-primary text-primary-foreground border-primary' : 'bg-secondary text-muted-foreground border-border'}`}>$</button>
              <Input type="number" className={inputCls}
                value={form.discountType === 'percent' ? form.percent_off : form.amount_off_usd}
                onChange={e => setForm(form.discountType === 'percent'
                  ? { ...form, percent_off: Number(e.target.value) }
                  : { ...form, amount_off_usd: Number(e.target.value) })} />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground block mb-1.5">Duration</label>
            <select value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value as any })}
              className="w-full h-10 px-3 rounded-xl bg-slate-900/60 border border-slate-700 text-foreground text-sm">
              <option value="once">Once (first invoice)</option>
              <option value="repeating">Repeating (months)</option>
              <option value="forever">Forever</option>
            </select>
          </div>

          {form.duration === 'repeating' && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5">Months</label>
              <Input type="number" min={1} max={36} value={form.duration_in_months}
                onChange={e => setForm({ ...form, duration_in_months: Number(e.target.value) })} className={inputCls} />
            </div>
          )}

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5">Max Uses</label>
            <Input type="number" min={1} placeholder="∞" value={form.max_redemptions || ''}
              onChange={e => setForm({ ...form, max_redemptions: Number(e.target.value) })} className={inputCls} />
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1.5">Expires</label>
            <Input type="date" value={form.expires_at}
              onChange={e => setForm({ ...form, expires_at: e.target.value })} className={inputCls} />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Input placeholder="Notes — who is this for? (e.g. Springfield Primary — cash, 30 seats, school year)"
            value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={inputCls} />
          <Button type="submit" disabled={creating}
            className="h-10 px-6 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase tracking-widest text-[11px] rounded-xl whitespace-nowrap">
            {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Generate Promo
          </Button>
        </div>

        {message && (
          <div className={`text-xs font-bold rounded-xl px-4 py-3 border ${message.kind === 'ok'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {message.text}
          </div>
        )}
      </form>

      {/* List */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-400">
              <Users className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-black text-white text-sm uppercase tracking-tight">Issued Codes ({promos.length})</h3>
              <p className="text-[11px] text-slate-400 font-medium">Live redemption counts synced from Stripe</p>
            </div>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
            <Input placeholder="Filter by code or note…" value={search}
              onChange={e => setSearch(e.target.value)} className={`pl-10 ${inputCls}`} />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center rounded-3xl bg-card/60 border border-border text-muted-foreground italic text-sm">
            No promo codes yet — create your first one above.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(p => {
              const pct = p.max_redemptions ? Math.min(100, (p.times_redeemed / p.max_redemptions) * 100) : 0
              return (
                <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={`bg-card border rounded-3xl p-6 shadow-sm transition-all ${p.is_active ? 'border-border hover:border-emerald-500/40' : 'border-border/50 opacity-60'}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-xl font-black text-foreground tracking-tighter">{p.code}</h4>
                        <button onClick={() => copy(p.code)} className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary" title="Copy code">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          {p.percent_off != null ? `${p.percent_off}% OFF` : `$${p.amount_off_usd} OFF`}
                        </span>
                        <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                          {p.duration === 'repeating' ? `${DURATION_LABEL[p.duration]} ×${p.duration_in_months}mo` : DURATION_LABEL[p.duration] || p.duration}
                        </span>
                        {!p.is_active && (
                          <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                            Deactivated
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => handleToggle(p)} title={p.is_active ? 'Deactivate (stop redemptions)' : 'Reactivate'}
                      className={`p-2 rounded-xl border transition-all ${p.is_active
                        ? 'border-emerald-200 text-emerald-500 hover:bg-emerald-50'
                        : 'border-slate-200 text-slate-300 hover:bg-slate-50'}`}>
                      <Zap className={`w-4 h-4 ${p.is_active && 'fill-current'}`} />
                    </button>
                  </div>

                  {p.max_redemptions ? (
                    <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100 mb-3">
                      <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                        <span>Redemptions</span>
                        <span className="text-slate-400">{p.times_redeemed} / {p.max_redemptions}</span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100 mb-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      {p.times_redeemed} redemptions · unlimited uses
                    </div>
                  )}

                  {p.notes && <p className="text-[11px] text-slate-400 italic mb-2">📝 {p.notes}</p>}

                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pt-3 border-t border-slate-100">
                    {p.expires_at ? `Expires ${new Date(p.expires_at).toLocaleDateString('en-GB')}` : 'No expiry'} · Created {new Date(p.created_at).toLocaleDateString('en-GB')}
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}